import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AgeVerification from '../components/AgeVerification';
import CookieConsent from '../components/CookieConsent';
import { Trophy, ExternalLink, Star, CheckCircle, XCircle, Award, Users, Clock, Shield, BarChart, Crown } from 'lucide-react';

const PubReview = () => {
  return (
    <div className="min-h-screen bg-book-cream" itemScope itemType="https://schema.org/Review">
      <Header />
      <main className="book-section">
        <div className="max-w-4xl mx-auto" itemProp="reviewBody">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-4 mb-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <Trophy className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-book-brown mb-2" itemProp="name">
                  PUB Casino Review
                </h1>
            <div className="flex items-center justify-center space-x-2" itemScope itemType="https://schema.org/Rating">
                   <div className="flex items-center space-x-1">
                     {[1, 2, 3, 4, 5, 6, 7, 8].map((star) => (
                       <Star
                         key={star}
                         className={`h-5 w-5 ${star <= 8 ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                       />
                     ))}
                   </div>
                   <span className="text-2xl font-bold text-book-brown" itemProp="ratingValue">8.5</span>
                   <meta itemProp="bestRating" content="10" />
                 </div>
              </div>
            </div>
            <p className="text-xl text-book-brown-dark leading-relaxed max-w-3xl mx-auto" itemProp="description">
              A beloved UK casino brand that brings the authentic pub atmosphere to online gaming, combining community spirit with generous welcome bonuses and classic casino entertainment.
            </p>
            <div itemScope itemType="https://schema.org/Organization" itemProp="author">
              <meta itemProp="name" content="Brit Ember" />
            </div>
            <meta itemProp="datePublished" content="2025-12-16" />
          </div>

          {/* Featured Image Placeholder */}
          <div className="mb-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl p-8 text-center text-white">
            <img
              src="/images/aceofnet-gTp5dGK6jtY-unsplash.webp"
              alt="Winamax Gaming Platform"
              className="w-full h-64 object-cover rounded-lg"
              loading="lazy"
            />
            <p className="text-sm opacity-75 mt-4">France's Premier Betting Destination</p>
          </div>

          {/* Our Review Methodology */}
          <div className="mb-16 bg-book-beige rounded-xl p-8 shadow-book-inset">
            <h2 className="text-2xl font-bold text-book-brown mb-6 text-center">Our Review Methodology</h2>
            <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
              <p className="mb-4">
                Every casino review published on Brit Ember represents the culmination of comprehensive evaluation
                methodology that combines quantitative analysis, qualitative assessment, and real-world testing.
                Our systematic approach ensures that every recommendation delivers genuine value to discerning players.
              </p>
              <p className="mb-4">
                The review process begins with extensive platform analysis, examining licensing credentials,
                regulatory compliance, and operational infrastructure. We conduct thorough testing of payment
                systems, customer support quality, and platform stability to identify any potential issues
                before they impact players.
              </p>
              <p className="mb-4">
                Performance metrics form the foundation of our evaluations, with detailed analysis of payout
                speeds, game fairness, and platform reliability. We track actual withdrawal times, test customer
                support response quality, and monitor platform uptime to provide accurate performance data.
              </p>
              <p className="mb-4">
                User experience evaluation goes beyond surface-level impressions, incorporating accessibility
                testing, mobile performance analysis, and cross-device compatibility assessments. We examine
                how platforms perform under real-world conditions, including peak usage periods and network
                variability.
              </p>
              <p className="mb-4">
                Security and responsible gaming measures receive particular attention, with detailed analysis
                of player protection tools, financial safeguards, and self-exclusion options. We verify the
                effectiveness of these measures through comprehensive testing and validation procedures.
              </p>
              <p>
                Our commitment to transparency means every review clearly explains our methodology, highlights
                both strengths and limitations, and provides specific recommendations based on different player
                profiles. We maintain independence from casino operators, ensuring our evaluations serve player
                interests exclusively. This rigorous, methodical approach transforms subjective opinions into
                actionable intelligence that empowers informed gaming decisions.
              </p>
            </div>
          </div>

          {/* Main Review Content */}
          <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed mb-12">
             <h2 className="text-3xl font-bold text-book-brown mb-6">PUB Casino: Authentic UK Gaming Experience</h2>

             <p className="mb-6">
               PUB Casino represents the perfect fusion of traditional British pub culture with modern online gaming excellence.
               This beloved UK casino brand brings the warm, welcoming atmosphere of your local pub to the digital realm,
               offering genuine entertainment with a community-focused approach that resonates with British players.
             </p>

             <p className="mb-6">
               What sets PUB Casino apart is its unwavering commitment to authentic UK gaming values - fairness, community,
               and genuine enjoyment. Unlike many international operators, PUB Casino understands the British gaming
               landscape and delivers experiences that feel familiar and trustworthy to UK players seeking quality entertainment.
             </p>

             <h3 className="text-2xl font-bold text-book-brown mb-4">Generous Welcome Bonus</h3>

             <p className="mb-6">
               PUB Casino's 100% welcome bonus up to £100 represents outstanding value for new players. This generous offer
               effectively doubles your initial deposit, providing substantial additional funds to explore the casino's
               extensive game library. The bonus structure demonstrates PUB Casino's commitment to giving players a
               meaningful head start in their gaming journey.
             </p>

             <p className="mb-6">
               What makes this bonus particularly attractive is its straightforward 100% match on deposits up to £100,
               creating a maximum bonus value of £100. This approach ensures that players of all budgets can benefit
               from the welcome offer, making premium casino entertainment accessible to a broader audience while
               maintaining the quality and fairness that PUB Casino is known for.
             </p>

             <h3 className="text-2xl font-bold text-book-brown mb-4">Extensive Game Selection</h3>

             <p className="mb-6">
               PUB Casino boasts an impressive collection of casino games that cater to every taste and preference. From
               classic table games like blackjack and roulette to modern video slots with stunning graphics and innovative
               features, the platform ensures that players never run out of entertainment options. This diverse selection
               reflects PUB Casino's understanding that different players seek different experiences.
             </p>

             <p className="mb-6">
               The game library includes titles from leading software providers, ensuring high-quality graphics, smooth
               gameplay, and fair outcomes. Whether you're a fan of traditional casino games or prefer the excitement of
               modern video slots, PUB Casino delivers a comprehensive gaming experience that maintains the authentic
               feel of a British pub while offering the convenience of online play.
             </p>

             <h3 className="text-2xl font-bold text-book-brown mb-4">Community-Focused Gaming</h3>

             <p className="mb-6">
               What truly distinguishes PUB Casino is its strong sense of community and authentic UK gaming culture.
               The platform fosters a welcoming environment that feels like your local pub, where players can enjoy
               casino games in a comfortable, familiar atmosphere. This community approach extends beyond mere gaming
               to create genuine connections among players who share similar interests.
             </p>

             <p className="mb-6">
               PUB Casino's commitment to community is reflected in its fair play policies, transparent operations,
               and genuine concern for player welfare. Unlike many casinos that prioritize profits over people, PUB
               Casino maintains the traditional British values of honesty, fairness, and community spirit that have
               made it a beloved gaming destination.
             </p>

             <h3 className="text-2xl font-bold text-book-brown mb-4">Reliable Customer Support</h3>

             <p className="mb-6">
               PUB Casino's customer support team embodies the friendly, helpful spirit associated with traditional British pubs.
               Support staff are knowledgeable about casino games, bonus terms, and technical issues, providing assistance
               that feels personal and genuine rather than corporate and scripted. This authentic approach to customer
               service enhances the overall gaming experience.
             </p>

             <p className="mb-6">
               The support team is available through multiple channels, ensuring players can get help when they need it.
               Whether you have questions about bonuses, game rules, or technical issues, PUB Casino's support team
               provides reliable assistance that maintains the platform's reputation for fairness and player care.
             </p>
          </div>

          {/* Charts Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-book-brown mb-8 text-center">Winamax Performance Analytics</h2>

             <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
               {/* Chart 1 */}
               <div className="bg-white rounded-xl shadow-book p-6">
                 <h3 className="text-xl font-bold text-book-brown mb-4 text-center">Game Variety</h3>
                 <div className="flex justify-center mb-4">
                   <div className="relative w-48 h-48">
                     <svg viewBox="0 0 100 100" className="w-full h-full">
                       <circle cx="50" cy="50" r="40" fill="none" stroke="#FFD700" strokeWidth="20"/>
                       <circle cx="50" cy="50" r="40" fill="none" stroke="#8B4513" strokeWidth="20" strokeDasharray="125.66" strokeDashoffset="25.13"/>
                     </svg>
                     <div className="absolute inset-0 flex items-center justify-center">
                       <span className="text-2xl font-bold text-book-brown">500+</span>
                     </div>
                   </div>
                 </div>
                 <div className="text-center text-sm text-book-brown-dark">
                   Casino games available
                 </div>
               </div>

               {/* Chart 2 */}
               <div className="bg-white rounded-xl shadow-book p-6">
                 <h3 className="text-xl font-bold text-book-brown mb-4 text-center">Bonus Value</h3>
                 <div className="space-y-4">
                   <div className="space-y-3">
                     <div className="flex items-center justify-between">
                       <span className="text-book-brown-dark">Welcome Bonus</span>
                       <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                         <div className="bg-green-500 h-3 rounded-full" style={{width: '100%'}}></div>
                       </div>
                       <span className="font-semibold text-green-600">£100 max</span>
                     </div>
                     <div className="flex items-center justify-between">
                       <span className="text-book-brown-dark">Bonus Percentage</span>
                       <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                         <div className="bg-green-500 h-3 rounded-full" style={{width: '100%'}}></div>
                       </div>
                       <span className="font-semibold text-green-600">100%</span>
                     </div>
                     <div className="flex items-center justify-between">
                       <span className="text-book-brown-dark">Fair Play Rating</span>
                       <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                         <div className="bg-green-500 h-3 rounded-full" style={{width: '95%'}}></div>
                       </div>
                       <span className="font-semibold text-green-600">Excellent</span>
                     </div>
                   </div>
                 </div>
               </div>

               {/* Chart 3 */}
               <div className="bg-white rounded-xl shadow-book p-6">
                 <h3 className="text-xl font-bold text-book-brown mb-4 text-center">Game Categories</h3>
                 <div className="space-y-4">
                   <div className="space-y-3">
                     <div className="flex items-center justify-between">
                       <span className="text-book-brown-dark">Video Slots</span>
                       <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                         <div className="bg-green-500 h-3 rounded-full" style={{width: '95%'}}></div>
                       </div>
                       <span className="font-semibold text-green-600">400+ games</span>
                     </div>
                     <div className="flex items-center justify-between">
                       <span className="text-book-brown-dark">Table Games</span>
                       <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                         <div className="bg-green-500 h-3 rounded-full" style={{width: '85%'}}></div>
                       </div>
                       <span className="font-semibold text-green-600">50+ games</span>
                     </div>
                     <div className="flex items-center justify-between">
                       <span className="text-book-brown-dark">Live Casino</span>
                       <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                         <div className="bg-blue-500 h-3 rounded-full" style={{width: '75%'}}></div>
                       </div>
                       <span className="font-semibold text-blue-600">25+ tables</span>
                     </div>
                   </div>
                 </div>
               </div>
            </div>
          </div>

          {/* Pros and Cons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-green-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-green-800 mb-4 flex items-center">
                <CheckCircle className="h-6 w-6 mr-2" />
                Strengths
              </h3>
               <ul className="space-y-3">
                 <li className="flex items-start">
                   <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                   <span className="text-green-700">Authentic UK gaming experience with community focus</span>
                 </li>
                 <li className="flex items-start">
                   <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                   <span className="text-green-700">Generous 100% welcome bonus up to £100</span>
                 </li>
                 <li className="flex items-start">
                   <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                   <span className="text-green-700">Extensive game library with 500+ casino games</span>
                 </li>
                 <li className="flex items-start">
                   <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                   <span className="text-green-700">Fair play guarantee and transparent operations</span>
                 </li>
                 <li className="flex items-start">
                   <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                   <span className="text-green-700">Reliable customer support with genuine care</span>
                 </li>
               </ul>
            </div>

            <div className="bg-red-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-red-800 mb-4 flex items-center">
                <XCircle className="h-6 w-6 mr-2" />
                Limitations
              </h3>
               <ul className="space-y-3">
                 <li className="flex items-start">
                   <XCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                   <span className="text-red-700">Limited live casino options compared to premium competitors</span>
                 </li>
                 <li className="flex items-start">
                   <XCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                   <span className="text-red-700">Mobile app could be more feature-rich</span>
                 </li>
                 <li className="flex items-start">
                   <XCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                   <span className="text-red-700">Bonus terms require careful reading</span>
                 </li>
               </ul>
            </div>
          </div>

          {/* Our Verdict */}
          <div className="bg-gradient-to-r from-book-brown to-book-brown-light rounded-xl p-8 text-book-cream mb-12">
             <h3 className="text-2xl font-bold mb-6 flex items-center">
               <Award className="h-7 w-7 mr-3" />
               Our Verdict: 8.5/10
             </h3>
             <div className="prose prose-lg max-w-none text-book-cream leading-relaxed">
               <p className="mb-4">
                 PUB Casino represents the perfect blend of authentic British gaming culture with modern online casino excellence.
                 This beloved UK brand successfully brings the welcoming atmosphere of traditional pubs to the digital gaming space,
                 creating an experience that feels familiar and trustworthy to British players.
               </p>
               <p className="mb-4">
                 The platform's 100% welcome bonus up to £100 provides outstanding value for new players, effectively doubling
                 their initial gaming budget. Combined with an extensive game library featuring over 500 casino games, PUB Casino
                 offers genuine entertainment that prioritizes player enjoyment over aggressive marketing tactics.
               </p>
               <p className="mb-4">
                 What truly sets PUB Casino apart is its commitment to fair play and community values. Unlike many international
                 operators that can feel impersonal, PUB Casino maintains the traditional British emphasis on honesty, fairness,
                 and genuine customer care that has made it a beloved gaming destination.
               </p>
               <p>
                 For players seeking authentic UK gaming experiences with generous bonuses and fair play, PUB Casino represents
                 an excellent choice. While it may not have the most extensive live casino offerings, its commitment to quality
                 gaming and community values makes it a standout option for discerning British players who value integrity
                 alongside entertainment.
               </p>
             </div>
          </div>

          {/* Casino Link */}
          <div className="text-center mb-12">
            <a
              href="https://www.pubcasino.co.uk/"
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              className="inline-flex items-center space-x-3 bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-semibold text-lg shadow-book"
            >
              <span>Visit PUB Casino</span>
              <ExternalLink className="h-5 w-5" />
            </a>
            <p className="text-sm text-book-brown-dark mt-3">
              Official PUB Casino website - Authentic UK gaming experience
            </p>
          </div>

          {/* Related Reviews */}
          <div className="border-t border-book-brown-light pt-12">
            <h3 className="text-2xl font-bold text-book-brown mb-6 text-center">Explore Other Reviews</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <a
                href="/reviews/nrg"
                data-discover="true"
                className="bg-white rounded-lg p-6 shadow-book hover:shadow-lg transition-shadow duration-200"
              >
                 <div className="flex items-center space-x-3 mb-3">
                   <Star className="h-6 w-6 text-green-600" />
                   <h4 className="text-lg font-bold text-book-brown">NRG Review</h4>
                 </div>
                 <p className="text-book-brown-dark text-sm">High-energy gaming platform with 200% bonus and 100 free spins...</p>
               </a>
              <a
                href="/reviews/mrplay"
                data-discover="true"
                className="bg-white rounded-lg p-6 shadow-book hover:shadow-lg transition-shadow duration-200"
              >
                 <div className="flex items-center space-x-3 mb-3">
                   <Crown className="h-6 w-6 text-purple-600" />
                   <h4 className="text-lg font-bold text-book-brown">Mr. Play Review</h4>
                 </div>
                 <p className="text-book-brown-dark text-sm">Sophisticated gaming with 100 spins and 100% up to £200 bonus...</p>
               </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <AgeVerification />
      <CookieConsent />
    </div>
  );
};

export default PubReview;
